import { useMemo, useState } from "react";
import { useRoute, useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";
import { Loader2, Paperclip, Flag, Undo2, Play, ClipboardList, Save, ChevronRight, ChevronLeft, CheckCircle } from "lucide-react";
import { QA, useQaPerms, useOpenEvidence, QaCrumb } from "./qa-shared";

const FLAGS = [
  { v: "none", label: "None", color: QA.text3 },
  { v: "watch", label: "Watch", color: "#1D4ED8" },
  { v: "concern", label: "Concern", color: QA.amber },
  { v: "critical", label: "Critical", color: QA.red },
] as const;

const AVAIL_LABEL: Record<string, { t: string; c: string }> = {
  yes: { t: "Available", c: QA.green }, no: { t: "Not Available", c: QA.red },
};
const PRACTICE_LABEL: Record<string, { t: string; c: string }> = {
  yes: { t: "In Practice", c: QA.green }, no: { t: "Not Practiced", c: QA.red }, na: { t: "N/A", c: QA.purple },
};

export default function QaQagReview() {
  const [, params] = useRoute("/qa/visits/:id/review");
  const [, setLocation] = useLocation();
  const visitId = params?.id ? Number(params.id) : undefined;
  const perms = useQaPerms();
  const openEvidence = useOpenEvidence();

  const { data, isLoading, refetch } = trpc.qa.qag.getReview.useQuery({ visitId: visitId! }, { enabled: !!visitId });
  const saveNote = trpc.qa.qag.saveNote.useMutation();
  const startReview = trpc.qa.qag.startReview.useMutation({ onSuccess: () => { toast.success("Review started"); refetch(); } });
  const returnToDc = trpc.qa.qag.returnToDc.useMutation({ onSuccess: () => { toast.success("Returned to DC team"); setLocation(`/qa/visits/${visitId}`); } });

  const [noteDraft, setNoteDraft] = useState<Record<number, string>>({});
  const [activeDomainId, setActiveDomainId] = useState<number | null>(null);

  const firstDomainId = data?.domains?.[0]?.id;
  const currentDomainId = activeDomainId ?? firstDomainId;
  const currentDomain = data?.domains?.find((d: any) => d.id === currentDomainId);
  const currentQuestions = data?.questions?.filter((q: any) => q.domainId === currentDomainId) ?? [];

  const responseByQ = useMemo(() => {
    const m: Record<number, any> = {};
    for (const r of data?.responses ?? []) m[r.questionId] = r;
    return m;
  }, [data?.responses]);
  const notesByQ = useMemo(() => {
    const m: Record<number, any> = {};
    for (const n of data?.notes ?? []) m[n.questionId] = n;
    return m;
  }, [data?.notes]);
  const attByQ = useMemo(() => {
    const m: Record<number, any[]> = {};
    for (const a of data?.attachments ?? []) (m[a.questionId] ??= []).push(a);
    return m;
  }, [data?.attachments]);

  if (isLoading) return <div className="flex h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" style={{ color: QA.purple }} /></div>;
  if (!data) return <div className="p-6 text-sm text-gray-500">Visit not found.</div>;

  const canManage = perms.reviewManage;
  const status = data.visit.status;

  return (
    <div className="qa-premium full">
      <div className="wrap" style={{ paddingTop: 22 }}>
        <QaCrumb backTo={`/qa/visits/${visitId}`} items={[{ label: "Visits", to: "/qa/visits" }, { label: data.visit?.visitNumber ?? `Visit #${visitId}`, to: `/qa/visits/${visitId}`, mono: true }, { label: "QAG Review" }]} />

        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-[20px] font-bold" style={{ color: QA.text }}>QAG Pre-Visit Review</h1>
            <p className="text-[13px]" style={{ color: QA.text2 }}>Review submitted answers & evidence; add internal notes and flags.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {canManage && status === "questionnaire_submitted" && (
              <button onClick={() => startReview.mutate({ visitId: visitId! })} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold text-white" style={{ background: QA.purple }}>
                <Play className="h-4 w-4" /> Start Review
              </button>
            )}
            {canManage && ["under_review"].includes(status) && perms.assessmentView && (
              <button onClick={() => setLocation(`/qa/visits/${visitId}/assessment`)} className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13px] font-semibold text-white" style={{ background: QA.purple }}>
                <ClipboardList className="h-4 w-4" /> Go to Assessment
              </button>
            )}
            {canManage && ["questionnaire_submitted", "under_review"].includes(status) && (
              <button onClick={() => { const reason = prompt("Reason for returning to DC team?") ?? undefined; returnToDc.mutate({ visitId: visitId!, reason }); }}
                className="inline-flex items-center gap-1.5 rounded-lg border px-4 py-2 text-[13px] font-semibold" style={{ borderColor: QA.border, color: QA.amber }}>
                <Undo2 className="h-4 w-4" /> Send Back
              </button>
            )}
          </div>
        </div>

        {/* Domain Tabs */}
        <div className="mb-6 flex gap-2 border-b" style={{ borderColor: QA.border, overflowX: "auto" }}>
          {data.domains.map((d: any) => (
            <button
              key={d.id}
              onClick={() => setActiveDomainId(d.id)}
              className="px-4 py-3 text-[13px] font-semibold whitespace-nowrap border-b-2 transition-colors"
              style={{
                borderColor: currentDomainId === d.id ? QA.purple : "transparent",
                color: currentDomainId === d.id ? QA.purple : QA.text2
              }}>
              {d.name}
            </button>
          ))}
        </div>

        {/* Questions for Current Domain */}
        {currentDomain && (
          <div className="mb-6">
            {currentQuestions.length === 0 ? (
              <div className="text-center py-8" style={{ color: QA.text2 }}>No questions in this domain</div>
            ) : (
              <>
                {currentQuestions.map((q: any) => {
                  const r = responseByQ[q.id];
                  const note = notesByQ[q.id];
                  const atts = attByQ[q.id] ?? [];
                  const draftFlag = note?.preVisitFlag ?? "none";
                  const draftNote = noteDraft[q.id];
                  const hasUnsavedNote = draftNote !== undefined && draftNote !== (note?.internalNote ?? "");

                  return (
                    <div key={q.id} className="mb-4 rounded-lg border bg-white p-4" style={{ borderColor: QA.border }}>
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold" style={{ background: QA.purpleXPale, color: QA.purple }}>{q.questionNumber}</span>
                        <span className="flex-1 text-[13px]" style={{ color: QA.text }}>{q.text}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-9 text-[12px]">
                        {r?.availability && <Tag text={AVAIL_LABEL[r.availability]?.t} color={AVAIL_LABEL[r.availability]?.c} />}
                        {r?.beingInPractice && <Tag text={PRACTICE_LABEL[r.beingInPractice]?.t} color={PRACTICE_LABEL[r.beingInPractice]?.c} />}
                        {atts.map((a) => (
                          <button key={a.id} onClick={() => openEvidence(a.fileKey)} className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] hover:underline" style={{ borderColor: QA.border, color: QA.text2 }}>
                            <Paperclip className="h-3 w-3" /> {a.fileName}
                          </button>
                        ))}
                      </div>
                      {(r?.availabilityReason || r?.beingInPracticeReason || r?.specificRemarks) && (
                        <div className="mt-2 space-y-1 pl-9 text-[12px]" style={{ color: QA.text2 }}>
                          {r?.availabilityReason && <div><b>Availability reason:</b> {r.availabilityReason}</div>}
                          {r?.beingInPracticeReason && <div><b>Practice reason:</b> {r.beingInPracticeReason}</div>}
                          {r?.specificRemarks && <div><b>Remarks:</b> {r.specificRemarks}</div>}
                        </div>
                      )}

                      {/* QAG note + flag */}
                      <div className="mt-3 pl-9">
                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          <Flag className="h-3.5 w-3.5" style={{ color: QA.text3 }} />
                          {FLAGS.map((f) => (
                            <button key={f.v} disabled={!canManage}
                              onClick={() => saveNote.mutate({ visitId: visitId!, questionId: q.id, internalNote: noteDraft[q.id] ?? note?.internalNote ?? null, preVisitFlag: f.v }, { onSuccess: () => refetch() })}
                              className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold disabled:opacity-50"
                              style={draftFlag === f.v ? { background: f.color, color: "#fff" } : { background: QA.grayPale, color: f.color }}>
                              {f.label}
                            </button>
                          ))}
                        </div>
                        {canManage && (
                          <div className="flex gap-2 items-start">
                            <textarea
                              value={draftNote !== undefined ? draftNote : (note?.internalNote ?? "")}
                              placeholder="Internal QAG note (not visible to DC team)…"
                              onChange={(e) => setNoteDraft((s) => ({ ...s, [q.id]: e.target.value }))}
                              className="flex-1 rounded-md border px-2.5 py-1.5 text-[12px] outline-none resize-none"
                              rows={2}
                              style={{ borderColor: QA.border }} />
                            <button
                              disabled={!hasUnsavedNote || saveNote.isPending}
                              onClick={() => {
                                const noteValue = noteDraft[q.id] ?? note?.internalNote ?? null;
                                saveNote.mutate({ visitId: visitId!, questionId: q.id, internalNote: noteValue, preVisitFlag: draftFlag }, {
                                  onSuccess: () => {
                                    setNoteDraft((s) => {
                                      const newState = { ...s };
                                      delete newState[q.id];
                                      return newState;
                                    });
                                    refetch();
                                    toast.success("Note saved");
                                  }
                                });
                              }}
                              className="mt-1 px-3 py-1.5 rounded-md text-[12px] font-semibold inline-flex items-center gap-1.5 text-white disabled:opacity-50"
                              style={{ background: hasUnsavedNote ? QA.purple : QA.text3 }}>
                              <Save className="h-3.5 w-3.5" /> Save
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Navigation Footer */}
                <div className="mt-8 flex justify-between items-center pt-6 border-t" style={{ borderColor: QA.border }}>
                  <button
                    onClick={() => {
                      const currentIndex = data.domains.findIndex((d: any) => d.id === currentDomainId);
                      if (currentIndex > 0) {
                        setActiveDomainId(data.domains[currentIndex - 1].id);
                      }
                    }}
                    disabled={data.domains.findIndex((d: any) => d.id === currentDomainId) === 0}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-[13px] font-semibold disabled:opacity-50"
                    style={{ borderColor: QA.border, color: QA.text2 }}>
                    <ChevronLeft className="h-4 w-4" /> Previous Domain
                  </button>

                  <div className="text-[12px]" style={{ color: QA.text3 }}>
                    {data.domains.findIndex((d: any) => d.id === currentDomainId) + 1} of {data.domains.length}
                  </div>

                  {data.domains.findIndex((d: any) => d.id === currentDomainId) === data.domains.length - 1 ? (
                    <button
                      onClick={() => setLocation(`/qa/visits/${visitId}`)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-white"
                      style={{ background: QA.green }}>
                      <CheckCircle className="h-4 w-4" /> Review Complete
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const currentIndex = data.domains.findIndex((d: any) => d.id === currentDomainId);
                        if (currentIndex < data.domains.length - 1) {
                          setActiveDomainId(data.domains[currentIndex + 1].id);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold text-white"
                      style={{ background: QA.purple }}>
                      Next Domain <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Tag({ text, color }: { text?: string; color?: string }) {
  if (!text) return null;
  return <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold" style={{ background: `${color}1a`, color }}>{text}</span>;
}
